import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'
import legalEntities from 'modules/core/store/collections/legalEntities'
import { employeeUser } from 'modules/core/store/collections/users'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { translatedIncidenceName } from 'modules/bookkeepers/lib/dynamicTranslations'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import {
  EMPLOYEE_CHANGE_TYPES,
  INCIDENCE_TYPES,
  LEAVE_CHANGE_TYPES,
} from 'modules/payroll_bundle/lib/Incidences/Types'

export const EMPLOYEE_TRACKED_CHANGES: Record<string, string[]> = {
  name: ['first_name', 'last_name'],
  id: ['identifier', 'identifier_type', 'social_security_number'],
  address: ['address_line_1', 'address_line_2', 'city', 'country', 'postal_code', 'state'],
  phone_number: ['phone_number'],
  bank: ['bank_number', 'swift_bic'],
  irpf: ['irpf_cents', 'irpf'],
  nationality: ['nationality'],
  gender: ['gender'],
  termination: ['terminated_on', 'termination_reason_type'],
  residence: ['is_resident'],
  workplace: ['location_name'],
  legal_entity: ['legal_entity_id'],
  tax_id: ['tax_id'],
  email: ['email'],
}

export const MESSAGE_SENDER = {
  bookkeeper: 'bookkeeper',
  admin: 'admin',
}

type IncidenceName = keyof typeof INCIDENCE_TYPES

export interface IIncidence {
  id: number
  employee_id: number | null
  legal_entity_id: number
  name: IncidenceName
  custom_name: string | null
  target_id: number
  starts_on: string | null
  ends_on: string | null
  read_at: string | null
  status: string
  company_id: number
  has_message: boolean | null
  message_from: string | null
  employee_full_name: string | null
  employee_name: string | null
  employee_surname: string | null
  legal_entity_name: string | null
  bookkeeper_view?: boolean
  is_reopened: boolean | null
  tax_id: string | null
}

class Incidence extends Model<IIncidence> {
  constructor(attributes: IIncidence) {
    super(attributes)
    makeObservable(this, {
      translatedName: computed,
      incidenceStatus: computed,
      employee: computed,
      legalEntity: computed,
      employeeFirstName: computed,
      employeeLastName: computed,
      legalEntityName: computed,
      isReopened: computed,
    })
  }

  static urlRoot() {
    return '/bookkeepers_management/incidences'
  }

  get employee(): Employee | undefined {
    const employeeId = this.attributes.employee_id

    return employees.get(employeeId)
  }

  get legalEntity(): LegalEntity {
    const legalEntityId = this.attributes.legal_entity_id
    const legalEntity = legalEntities.get(legalEntityId)

    if (!legalEntity) {
      throw Error(`Error getting legalEntity with id = ${legalEntityId}`)
    }

    return legalEntity
  }

  get translatedName(): string {
    const incidenceType = INCIDENCE_TYPES[this.attributes.name]

    if (this.attributes.custom_name) {
      return this.attributes.custom_name
    }

    return translatedIncidenceName(incidenceType)
  }

  get incidenceStatus(): string {
    return this.attributes.status || ''
  }

  get incidenceType(): string {
    const incidenceType = Object.keys(INCIDENCE_TYPES).find(
      (key) => INCIDENCE_TYPES[key as IncidenceName] === this.attributes.name
    )

    return incidenceType || ''
  }

  get isManualIncidence(): boolean {
    return this.incidenceType === INCIDENCE_TYPES.manual_incidence
  }

  get isLeaveIncidence(): boolean {
    return Object.keys(LEAVE_CHANGE_TYPES).includes(this.incidenceType) ?? false
  }

  // TODO: make -get incidencetype- when there are more types
  get isEmployeeDataChangeType(): boolean {
    const incidenceType = this.incidenceType

    return incidenceType in EMPLOYEE_CHANGE_TYPES
  }

  get isManual(): boolean {
    return this.attributes.name === INCIDENCE_TYPES.manual_incidence
  }

  get employeeFirstName(): string | null | undefined {
    return this.has('employee_name')
      ? this.get('employee_name')
      : this.employee && employeeUser(this.employee)?.firstName
  }

  get employeeLastName(): string | null | undefined {
    return this.has('employee_surname')
      ? this.get('employee_surname')
      : this.employee && employeeUser(this.employee)?.lastName
  }

  get legalEntityName(): string | null | undefined {
    return this.legalEntity?.name
  }

  get isInPreparation(): boolean {
    return this.get('status') === INCIDENCE_STATUSES.in_preparation
  }

  get isReopened(): boolean {
    return this.get('is_reopened') ?? false
  }

  isStatusUpdatable(adminView: boolean): boolean {
    if (adminView) {
      const currentStatus = this.attributes.status
      return (
        currentStatus === INCIDENCE_STATUSES.in_preparation ||
        currentStatus === INCIDENCE_STATUSES.discarded
      )
    } else return this.attributes.status !== INCIDENCE_STATUSES.in_preparation
  }

  hasMessage(isBookkeeper: boolean): boolean {
    return (
      (this.attributes.has_message &&
        (isBookkeeper
          ? this.attributes.message_from === MESSAGE_SENDER.admin
          : this.attributes.message_from === MESSAGE_SENDER.bookkeeper)) ??
      false
    )
  }
}

export default Incidence
